import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiCheckCircle, FiInfo, FiLoader } from "react-icons/fi";
import { useApi } from "../../Common/Hooks/useApi";
import { useBusiness } from "../../Common/Hooks/useBusiness";
import { DueDiligencePlanOffer } from "../../Common/Hooks/useApi/Schemas";
import { DueDiligenceTier } from "../../Common/Types-DTOs/DueDiligence";
import { isErr } from "../../Common/Utils/Result";
import { planNameKey } from "../../Common/Utils/dueDiligencePlans";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../../Components/Notifications";
import { DueDiligenceOneOffPrices, DueDiligencePlanPicker } from "../../Components/DueDiligencePlanPicker";
import { Page, Content, Title, Subtitle, Banner, Actions, ContinueButton, BackLink } from "./Styles";
import { ChangePlanConfirmModal } from "./ChangePlanConfirmModal";

/** The Stripe webhook activates the plan a few seconds after the redirect: ask until it shows. */
const ACTIVATION_POLL_INTERVAL_MS = 2000;
const ACTIVATION_POLL_ATTEMPTS = 15;

/** Pick a DD plan and go to Stripe to subscribe; Stripe sends the user back here. */
export const DueDiligencePlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { business, resyncBusiness } = useBusiness();
  const { getDueDiligencePlansEndpoint, getDueDiligencePlanCheckoutEndpoint, getDueDiligencePricingEndpoint } = useApi();
  const [plans, setPlans] = useState<DueDiligencePlanOffer[] | null>(null);
  const [oneOffPrices, setOneOffPrices] = useState<DueDiligenceOneOffPrices | null>(null);
  const [creditCosts, setCreditCosts] = useState<Record<DueDiligenceTier, number> | null>(null);
  const [hasFailedLoading, setHasFailedLoading] = useState(false);
  const [selectedLookupKey, setSelectedLookupKey] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isConfirmingChange, setIsConfirmingChange] = useState(false);
  /** The plan just paid for, until the webhook makes it the active one. */
  const [changingTo, setChangingTo] = useState<string | null>(null);

  const paymentStatus = searchParams.get("payment");
  const activePlan = business?.credits?.dueDiligencePlan ?? null;

  useEffect(() => {
    const loadPlans = async () => {
      const result = await getDueDiligencePlansEndpoint();
      if (isErr(result)) {
        setHasFailedLoading(true);
        return;
      }
      setPlans(result.value.plans);
    };
    const loadOneOffPrices = async () => {
      const result = await getDueDiligencePricingEndpoint();
      if (!isErr(result)) {
        setOneOffPrices(result.value.pricing);
        setCreditCosts(result.value.creditCosts);
      }
    };
    loadPlans();
    loadOneOffPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentStatus !== "success" || activePlan) {
      return;
    }

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      resyncBusiness();
      if (attempts >= ACTIVATION_POLL_ATTEMPTS) {
        clearInterval(interval);
      }
    }, ACTIVATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus, activePlan]);

  // After a change Stripe has charged, ask until the webhook brings the new plan
  useEffect(() => {
    if (!changingTo || activePlan?.lookupKey === changingTo) {
      if (changingTo) setChangingTo(null);
      return;
    }

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      resyncBusiness();
      if (attempts >= ACTIVATION_POLL_ATTEMPTS) {
        clearInterval(interval);
      }
    }, ACTIVATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changingTo, activePlan?.lookupKey]);

  const activeMonthlyCredits = plans?.find((plan) => plan.lookupKey === activePlan?.lookupKey)?.monthlyCredits;
  // With a plan, only the bigger ones can be picked: the change goes up only
  const unavailablePlans: Record<string, string> = {};
  if (activePlan && plans) {
    for (const plan of plans) {
      if (plan.lookupKey === activePlan.lookupKey) {
        unavailablePlans[plan.lookupKey] = t("dueDiligencePlans.changePlan.unavailable.current");
      } else if (activeMonthlyCredits !== undefined && plan.monthlyCredits <= activeMonthlyCredits) {
        unavailablePlans[plan.lookupKey] = t("dueDiligencePlans.changePlan.unavailable.lower");
      }
    }
  }
  const hasBiggerPlan = Boolean(plans && plans.some((plan) => !unavailablePlans[plan.lookupKey]));

  const handleContinue = async () => {
    if (!selectedLookupKey || isRedirecting) {
      return;
    }
    setIsRedirecting(true);
    const result = await getDueDiligencePlanCheckoutEndpoint(selectedLookupKey);
    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          result.error.errorCode === "DUE_DILIGENCE_PLAN_ALREADY_ACTIVE"
            ? t("dueDiligencePlans.page.notifications.alreadyActive")
            : t("dueDiligencePlans.page.notifications.checkoutFailed")
        )
      );
      setIsRedirecting(false);
      return;
    }
    window.location.href = result.value.url;
  };

  return (
    <Page>
      <Content>
        <BackLink onClick={() => navigate("/due-diligence")}>
          <FiArrowLeft size={13} />
          {t("dueDiligencePlans.page.backLink")}
        </BackLink>

        <div>
          <Title>{t("dueDiligencePlans.page.title")}</Title>
          <Subtitle>{t("dueDiligencePlans.page.subtitle")}</Subtitle>
        </div>

        {paymentStatus === "success" && !activePlan && (
          <Banner $variant="success">
            <FiLoader size={18} />
            {t("dueDiligencePlans.page.banners.activating")}
          </Banner>
        )}
        {paymentStatus === "cancelled" && !activePlan && (
          <Banner $variant="neutral">
            <FiInfo size={18} />
            {t("dueDiligencePlans.page.banners.cancelled")}
          </Banner>
        )}

        {activePlan && (
          <Banner $variant="success">
            {changingTo ? <FiLoader size={18} /> : <FiCheckCircle size={18} />}
            {changingTo
              ? t("dueDiligencePlans.changePlan.banners.changing", { plan: t(planNameKey(changingTo)) })
              : t("dueDiligencePlans.page.banners.active", {
                  plan: t(planNameKey(activePlan.lookupKey)),
                  count: business?.credits?.balance ?? 0,
                })}
          </Banner>
        )}
        {hasFailedLoading && (
          <Banner $variant="neutral">
            <FiInfo size={18} />
            {t("dueDiligencePlans.page.banners.loadFailed")}
          </Banner>
        )}
        {plans && (
          <DueDiligencePlanPicker
            plans={plans}
            selectedLookupKey={selectedLookupKey}
            onSelect={setSelectedLookupKey}
            oneOffPrices={oneOffPrices}
            creditCosts={creditCosts}
            unavailablePlans={unavailablePlans}
          />
        )}
        {plans && (!activePlan || hasBiggerPlan) && (
          <Actions>
            {activePlan ? (
              <ContinueButton
                onClick={() => setIsConfirmingChange(true)}
                disabled={!selectedLookupKey || Boolean(unavailablePlans[selectedLookupKey]) || Boolean(changingTo)}
              >
                {t("dueDiligencePlans.changePlan.changeButton")}
              </ContinueButton>
            ) : (
              <ContinueButton onClick={handleContinue} disabled={!selectedLookupKey || isRedirecting}>
                {isRedirecting && <FiLoader size={15} />}
                {t("dueDiligencePlans.page.continueButton")}
              </ContinueButton>
            )}
          </Actions>
        )}
        {isConfirmingChange && selectedLookupKey && (
          <ChangePlanConfirmModal
            lookupKey={selectedLookupKey}
            onClose={() => setIsConfirmingChange(false)}
            onChanged={(lookupKey) => {
              setIsConfirmingChange(false);
              setSelectedLookupKey(null);
              setChangingTo(lookupKey);
            }}
          />
        )}
      </Content>
    </Page>
  );
};
